import type { EditorialNextStepId } from '@/components/reach/editorial/editorial-next-step';
import { factCheckReleaseState } from '@/lib/content-doctrine/article-factcheck';
import type { ArticleReleaseInput } from '@/lib/content-doctrine/article-release';
import type { ArticleRevision } from '@/services/data/ArticleJobService';

/**
 * The article reader's header status line ("Version 3 · Checks passed · 2 notes open") and its one
 * primary action. Pure, so the words are tested once and the header, the rail and the table keep
 * reading the same derivations (`articleReviewState`, `factCheckReleaseState`, `editorialNextStep`).
 */

/** Ordinal -1 is the article a rewrite started from, so a rewrite can be undone. */
export function versionLabel(ordinal: number, kind?: 'address' | 'address_before'): string {
  if (kind === 'address') return 'Addressed review notes';
  if (kind === 'address_before') return 'Before addressing review notes';
  return ordinal < 0 ? 'Before the rewrite' : `Version ${ordinal + 1}`;
}

/** The current revision's label, or null when the job has recorded no revisions to number. */
export function currentVersionLabel(
  revisions: readonly Pick<ArticleRevision, 'ordinal' | 'kind' | 'isCurrent'>[] | null | undefined,
): string | null {
  const current = revisions?.find((revision) => revision.isCurrent);
  return current ? versionLabel(current.ordinal, current.kind) : null;
}

/**
 * Where the checks stand, in the order a person has to clear them: the draft checks first, then the
 * claim check. Null when no draft check has run (the status badge already says what to do).
 */
export function articleChecksLabel(release: ArticleReleaseInput): string | null {
  const web = release.webReadiness;
  if (!web) return null;
  if (!web.isCurrent) return 'Checks out of date';
  if (!web.ready) return 'Draft checks to fix';
  switch (factCheckReleaseState(release.factCheck)) {
    case 'unchecked':
      return 'Claims not checked';
    case 'stale':
      return 'Claims out of date';
    case 'failed':
      return 'Claims to fix';
    default:
      // `needs_review` clears the release gate (nothing is shown to be wrong), but some claims
      // could not be verified, so "passed" would overstate it beside a rail that says
      // "Needs review".
      return release.factCheck?.status === 'needs_review' ? 'Claims to review' : 'Checks passed';
  }
}

export function openNotesLabel(count: number): string | null {
  if (count <= 0) return null;
  return count === 1 ? '1 note open' : `${count} notes open`;
}

/** The status line's clauses, in reading order, with the empty ones left out. */
export function articleHeaderClauses(input: {
  release: ArticleReleaseInput;
  revisions?: readonly Pick<ArticleRevision, 'ordinal' | 'kind' | 'isCurrent'>[] | null;
}): string[] {
  return [
    currentVersionLabel(input.revisions),
    articleChecksLabel(input.release),
    openNotesLabel(input.release.unresolvedEditorialFindings),
  ].filter((clause): clause is string => Boolean(clause));
}

export type ArticleHeaderAction =
  { kind: 'review-notes'; label: string } | { kind: 'hand-off'; label: string };

/**
 * The one primary button the header offers for the next step, or null.
 *
 * Only steps the reader can carry out itself get a button: walking the open notes, and the hand-off
 * dialog (which also sends for approval). Writing, checking and claim checks run from the review rail,
 * where their cost and progress are explained; a header button that only opened the rail would claim
 * to do something it does not.
 */
export function articleHeaderAction(
  step: Exclude<EditorialNextStepId, 'all'>,
  openNotes: number,
): ArticleHeaderAction | null {
  if (step === 'resolve') {
    return openNotes > 0 ? { kind: 'review-notes', label: 'Review next note' } : null;
  }
  // The header does not read the approval state, so `send` and `hand-off` share one label: the
  // dialog itself offers sending for approval or recording the hand-off.
  if (step === 'send' || step === 'hand-off')
    return { kind: 'hand-off', label: 'Hand off article' };
  return null;
}

/** The open note after the focused one, wrapping round; the first when none is focused. */
export function nextNoteKey(
  keys: readonly string[],
  focusedKey: string | null | undefined,
): string | null {
  if (!keys.length) return null;
  const index = focusedKey ? keys.indexOf(focusedKey) : -1;
  return keys[(index + 1) % keys.length] ?? null;
}
